/**
 * Partner-subdomain host parsing.
 *
 * Shared by the edge middleware (`src/middleware.ts`) and the client referral
 * context (`ReferralCodeContext`) so there is a single source of truth for
 * which hosts map to a partner subdomain. Keep this dependency-free — it runs
 * in the edge runtime.
 *
 * Production partners live at `<label>.42a.ai`; local dev uses `<label>.localtest.me`
 * (which resolves `*.localtest.me` to 127.0.0.1).
 */

/** Labels that are never partner subdomains, even if a partner claims them. */
export const RESERVED_SUBDOMAINS = new Set([
  "www",
  "app",
  "api",
  "admin",
  "partners",
  "clerk",
  "accounts",
]);

/**
 * Reserved subdomains that route to a first-party standalone portal instead of
 * the regular app shell. The host itself decides which portal the visitor is
 * in — `admin.42a.ai` is the 42A operator console, `partners.42a.ai` is the
 * partner portal — and access is gated by role (operator/partner), never by
 * organization membership. Kept in `RESERVED_SUBDOMAINS` above so a white-label
 * partner can never claim them.
 */
export type Portal = "admin" | "partners";

const PORTAL_LABELS: Record<string, Portal> = {
  admin: "admin",
  partners: "partners",
};

/** Parent domains under which a single label is treated as a partner subdomain. */
const ROOT_DOMAINS = ["42a.ai", "localtest.me"];

/** Valid DNS label — lowercase alphanumerics and hyphens, 1-63 chars, no leading/trailing hyphen. */
export const DNS_LABEL_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Extract the partner subdomain label from a Host header / hostname.
 *
 * Returns `null` for every non-partner host: bare root domains, `localhost`,
 * Vercel preview domains (`*.vercel.app`), reserved labels, and multi-level
 * labels (`a.b.42a.ai`).
 *
 *   acme.42a.ai            -> "acme"
 *   acme.localtest.me:3000 -> "acme"
 *   42a.ai / www.42a.ai    -> null
 *   localhost / *.vercel.app -> null
 */
export function parsePartnerSubdomain(
  hostHeader: string | null | undefined,
): string | null {
  if (!hostHeader) return null;

  const host = hostHeader.split(":")[0].toLowerCase().trim(); // strip :port
  if (!host || host === "localhost" || host.endsWith(".vercel.app")) return null;

  const root = ROOT_DOMAINS.find((d) => host === d || host.endsWith("." + d));
  if (!root || host === root) return null; // unknown host or bare root domain

  const label = host.slice(0, host.length - root.length - 1); // drop ".<root>"
  if (!label || label.includes(".")) return null; // only single-level labels
  if (RESERVED_SUBDOMAINS.has(label)) return null;

  return label;
}

/**
 * Extract the standalone-portal identity from a Host header / hostname.
 *
 * Returns `"admin"` for `admin.42a.ai`, `"partners"` for `partners.42a.ai`
 * (and their `*.localtest.me` dev equivalents), and `null` for every other
 * host — bare root domains, the app host, partner white-label subdomains,
 * multi-level labels, `localhost`, and Vercel previews.
 *
 * This mirrors `parsePartnerSubdomain`'s host parsing but resolves the two
 * reserved portal labels rather than excluding them.
 *
 *   admin.42a.ai              -> "admin"
 *   partners.42a.ai           -> "partners"
 *   partners.localtest.me:3000 -> "partners"
 *   acme.42a.ai / 42a.ai      -> null
 *   localhost / *.vercel.app  -> null
 */
export function parsePortalHost(
  hostHeader: string | null | undefined,
): Portal | null {
  if (!hostHeader) return null;

  const host = hostHeader.split(":")[0].toLowerCase().trim(); // strip :port
  if (!host || host === "localhost" || host.endsWith(".vercel.app")) return null;

  const root = ROOT_DOMAINS.find((d) => host === d || host.endsWith("." + d));
  if (!root || host === root) return null; // unknown host or bare root domain

  const label = host.slice(0, host.length - root.length - 1); // drop ".<root>"
  if (!label || label.includes(".")) return null; // only single-level labels

  return PORTAL_LABELS[label] ?? null;
}

/**
 * Absolute origin for a standalone portal, derived from the CURRENT host so it
 * works in every environment without hardcoding domains. Used to build the
 * "open the admin/partner portal in a new tab" links in the user menu.
 *
 *   app.42a.ai / acme.42a.ai       -> https://<portal>.42a.ai
 *   admin.localtest.me:3000        -> http://<portal>.localtest.me:3000
 *   localhost:3000 / 127.0.0.1:3000 -> http://<portal>.localtest.me:3000  (dev)
 *   *.vercel.app / customer domain -> null  (no portal subdomain here)
 *
 * The label is swapped under the SAME root domain and port as the current host,
 * so a partner white-label host (`acme.42a.ai`) still points at the shared
 * `admin.42a.ai` / `partners.42a.ai`. On localhost we fall back to
 * `*.localtest.me` (which resolves to 127.0.0.1) so subdomain routing is
 * testable locally with zero DNS/hosts setup — just hit the returned URL.
 *
 * Returns `null` when the current host has no portal-subdomain concept (Vercel
 * previews, customer-owned custom domains); callers should fall back to a
 * same-origin path (`/admin`, `/partners`) in that case.
 */
function swapLabelOrigin(
  label: string,
  currentHost: string | null | undefined,
  protocol?: string | null,
): string | null {
  if (!currentHost) return null;

  const [rawHost, port] = currentHost.split(":");
  const host = rawHost.toLowerCase().trim();
  if (!host) return null;

  const scheme = protocol && protocol.length > 0 ? protocol : "https:";
  const portSuffix = port ? `:${port}` : "";

  // Known 42a-owned root: swap the label under the same root + port.
  const root = ROOT_DOMAINS.find((d) => host === d || host.endsWith("." + d));
  if (root) {
    return `${scheme}//${label}.${root}${portSuffix}`;
  }

  // Local dev: localhost / 127.0.0.1 -> *.localtest.me (resolves to 127.0.0.1),
  // so the sibling subdomain is reachable on the same dev server.
  if (host === "localhost" || host === "127.0.0.1") {
    return `${scheme}//${label}.localtest.me${portSuffix}`;
  }

  // Vercel previews and customer custom domains have no sibling-subdomain concept.
  return null;
}

export function portalOrigin(
  portal: Portal,
  currentHost: string | null | undefined,
  protocol?: string | null,
): string | null {
  return swapLabelOrigin(portal, currentHost, protocol);
}

/**
 * Absolute origin of the MAIN app host, derived from the current host — the
 * inverse of portalOrigin. From a portal host (admin.42a.ai / partners.42a.ai)
 * it returns the app host (`app.42a.ai` in prod, `app.localtest.me:<port>` in
 * dev). Used to open an organization in the full app (in a new tab) from a
 * portal's "Enter org" action. Returns null for hosts with no 42a-root mapping
 * (Vercel previews, customer custom domains); callers fall back to a same-origin
 * navigation there.
 */
export function appOrigin(
  currentHost: string | null | undefined,
  protocol?: string | null,
): string | null {
  return swapLabelOrigin("app", currentHost, protocol);
}

/**
 * Lowercase, port-stripped host from a Host header, or null if absent.
 *
 *   Acme.42a.ai:3000 -> "acme.42a.ai"
 */
export function normalizeHost(hostHeader: string | null | undefined): string | null {
  if (!hostHeader) return null;
  const host = hostHeader.split(":")[0].toLowerCase().trim();
  return host || null;
}

/**
 * True for every host that already resolves through 42a-owned infrastructure:
 * a known root domain (bare or subdomain), localhost, or a Vercel preview
 * deployment. Used to distinguish a genuine customer-owned custom domain
 * (`app.acme.com`) from hosts that are already handled by other paths.
 */
export function isKnown42aHost(host: string): boolean {
  if (host === "localhost" || host.endsWith(".vercel.app")) return true;
  return ROOT_DOMAINS.some((d) => host === d || host.endsWith("." + d));
}

/**
 * Extract a *candidate* customer-owned custom-domain host from a Host header.
 *
 * Returns the full host string when it is not a known 42a host (see
 * `isKnown42aHost`) — i.e. a candidate for the `by_custom_domain` Convex
 * lookup. This function only classifies the shape of the host; it never
 * confirms the domain is actually registered to a partner. Authority for
 * that rests entirely with the Convex `getPartnerByCustomDomain` /
 * `getPartnerBrandingByCustomDomain` queries, which return `null` for any
 * host that isn't an active, white-labeled partner's verified domain.
 *
 *   app.acme.com          -> "app.acme.com"
 *   acme.42a.ai            -> null (handled by parsePartnerSubdomain instead)
 *   localhost / *.vercel.app -> null
 */
export function parseCustomDomainHost(hostHeader: string | null | undefined): string | null {
  const host = normalizeHost(hostHeader);
  if (!host) return null;
  if (isKnown42aHost(host)) return null;
  return host;
}

/**
 * Where a CUSTOMER organization invitation should send its recipient.
 *
 * Org invites are issued from wherever the inviter happens to be — the admin
 * console (`admin.42a.ai`) or the partner portal (`partners.42a.ai`) — but the
 * recipient is a customer, and a portal host exposes only its own portal: a
 * customer landing on `partners.42a.ai/accept-invite` hits
 * `PortalAccessDeniedScreen` instead of joining the org. So rewrite a portal
 * origin to the app host; every other origin (the app host itself, a partner
 * white-label subdomain, a customer-owned domain, a preview deployment) is
 * already a customer-facing host and is returned untouched.
 *
 *   https://partners.42a.ai -> https://app.42a.ai
 *   https://admin.42a.ai    -> https://app.42a.ai
 *   https://acme.42a.ai     -> https://acme.42a.ai   (branded, stays put)
 */
export function customerInviteOrigin(origin: string): string {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return origin;
  }
  if (!parsePortalHost(url.host)) return origin;
  return appOrigin(url.host, url.protocol) ?? origin;
}
